import {useCallback} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  Flex,
  Image,
  Text,
  VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {PIM_SQUARE_THUMBNAIL_PX} from '@amblique/king-living/app/components/configuration-selector/utils';
import {CUSTOMIZE_STEP_LAYOUT_KIND} from '@amblique/king-living/app/hooks/use-customize-steps';
import {formatShelvesSelectionName, sfccProductIdDataAttrs} from '@amblique/king-living/app/utils/product-utils';

function buildSlotCardClassName({className, hasSelection, stepKind}) {
  const base = 'slot-card customize-steps-slot-card';
  const mod = stepKind ? ` customize-steps-slot-card--${stepKind}` : '';
  const selectedMod = hasSelection ? ' customize-steps-slot-card--selected' : '';

  return [base + mod + selectedMod, className].filter(Boolean).join(' ');
}

export function SlotCard({
  buttonRef,
  chooseButtonAriaLabel,
  className,
  emptyStateHint,
  isDisabled,
  isRequired,
  isSimplePDP = false,
  label,
  selection,
  slotIndex,
  stepKind,
  styles,
  value,
  onChoose,
}) {
  const intl = useIntl();
  const hasSelection = selection != null && (selection.name || selection.productId);
  const isEmpty = !hasSelection;
  const useCoverEmptyLayout = Boolean(isEmpty && emptyStateHint);
  const cardStyles = hasSelection ?
    {...styles?.slotCard, ...styles?.slotCardSelected} :
    {...styles?.slotCard, ...styles?.slotCardEmpty};

  const chooseActionLabel = intl.formatMessage(
    hasSelection ?
      {defaultMessage: 'Change', id: 'customize_panel.option_step.change'} :
      {defaultMessage: 'Choose', id: 'customize_panel.option_step.choose'}
  );
  const chooseAriaLabel = chooseButtonAriaLabel ??
    (typeof label === 'string' ? `${chooseActionLabel} ${label}` : undefined);

  const handleChoose = useCallback(() => {
    if (!isDisabled) {
      onChoose?.();
    }
  }, [isDisabled, onChoose]);

  const slotSfccIdAttrs =
    selection?.productId ?
      sfccProductIdDataAttrs(
        stepKind === CUSTOMIZE_STEP_LAYOUT_KIND.COVER ? 'variant' : 'option',
        selection.productId
      ) : {};

  return (
    <Box
      className={buildSlotCardClassName({className, hasSelection, stepKind})}
      data-customize-step-kind={stepKind}
      {...cardStyles}
      {...slotSfccIdAttrs}
      aria-disabled={isDisabled ? true : undefined}
      cursor={isDisabled ? undefined : 'pointer'}
      onClick={isDisabled ? undefined : handleChoose}
    >
      <Flex
        alignItems="center"
        className="slot-card-container"
        flex={1}
        gap={4}
        minWidth={0}
      >
        {hasSelection && selection.imageUrl && (
          <Image
            alt=""
            className="slot-card-image"
            htmlHeight={PIM_SQUARE_THUMBNAIL_PX}
            htmlWidth={PIM_SQUARE_THUMBNAIL_PX}
            src={selection.imageUrl}
            {...styles?.slotCardImage}
          />
        )}
        {hasSelection && isSimplePDP && !selection.imageUrl && (
          <Box
            aria-hidden
            className="slot-card-image slot-card-image--placeholder"
            role="presentation"
            {...styles?.slotCardImagePlaceholder}
          />
        )}
        <VStack
          alignItems="flex-start"
          className="slot-card-content-text"
          flex={1}
          minWidth={0}
          spacing={isEmpty ? '8px' : 2}
        >
          {useCoverEmptyLayout ? (
            <>
              <Text className="slot-card-content-text-empty-title" {...styles?.nameLineEmptyTitle}>
                <FormattedMessage
                  defaultMessage="No selection yet"
                  id="customize_panel.option_step.no_selection"
                />
              </Text>
              <Text className="slot-card-content-text-empty-hint" {...styles?.nameLineEmptyHint}>
                {emptyStateHint}
              </Text>
            </>
          ) : hasSelection ? (
            <>
              <Text className="slot-card-content-text-label" {...styles?.nameLine1}>
                {label}
              </Text>
              <Text className="slot-card-content-text-name" noOfLines={2} {...styles?.nameLine2}>
                {stepKind === CUSTOMIZE_STEP_LAYOUT_KIND.MULTI_TYPE ?
                  formatShelvesSelectionName(selection.name) :
                  selection.name}
              </Text>
            </>
          ) : (
            <>
              {label != null && label !== '' && (
                <Text className="slot-card-content-text-empty-slot-label" {...styles?.nameLineEmptySlotLabel}>
                  {label}
                </Text>
              )}
              <Text className="slot-card-content-text-empty-title" {...styles?.nameLineEmptyTitle}>
                <FormattedMessage
                  defaultMessage="No selection yet"
                  id="customize_panel.option_step.no_selection"
                />
              </Text>
            </>
          )}
        </VStack>
      </Flex>
      <Button
        ref={buttonRef}
        className="slot-card-choose-button"
        {...styles?.slotCardChooseLink}
        aria-label={chooseAriaLabel}
        isDisabled={isDisabled}
        minH="24px"
        minW="24px"
        p={0}
        px={0}
        py={0}
        size="sm"
        tabIndex={isDisabled ? -1 : 0}
        type="button"
        variant="ghost"
      >
        {hasSelection ? (
          <FormattedMessage
            defaultMessage="Change"
            id="customize_panel.option_step.change"
          />
        ) : (
          <FormattedMessage
            defaultMessage="Choose"
            id="customize_panel.option_step.choose"
          />
        )}
      </Button>
    </Box>
  );
}

SlotCard.displayName = 'SlotCard';

SlotCard.propTypes = {
  buttonRef: PropTypes.func,
  chooseButtonAriaLabel: PropTypes.string,
  className: PropTypes.string,
  stepKind: PropTypes.string,
  emptyStateHint: PropTypes.node,
  isDisabled: PropTypes.bool,
  isRequired: PropTypes.bool,
  isSimplePDP: PropTypes.bool,
  label: PropTypes.node,
  selection: PropTypes.object,
  slotIndex: PropTypes.number,
  styles: PropTypes.object.isRequired,
  value: PropTypes.object,
  onChoose: PropTypes.func.isRequired,
};
